import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d70id7r2m2156g4s6t10';

const FinancingPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new URLSearchParams(new FormData(e.currentTarget) as unknown as Record<string, string>);
    setStatus('submitting');
    try {
      const res = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: data.toString() });
      setStatus(res.ok ? 'success' : 'error');
    } catch { setStatus('error'); }
  };

  const options = [
    { term: '12 Months', apr: '0% APR', monthly: '$83/mo', total: '$999+', note: 'Best for smaller projects' },
    { term: '24 Months', apr: '0% APR', monthly: '$42/mo', total: '$999+', note: 'Most popular option' },
    { term: '60 Months', apr: '5.99% APR', monthly: '$19/mo', total: '$1,000+', note: 'Low monthly payment' },
    { term: '120 Months', apr: '7.99% APR', monthly: '$12/mo', total: '$1,000+', note: 'Maximum flexibility' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="bg-[#0C2340] py-20 nav-offset">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C41E3A] text-sm font-bold uppercase tracking-widest mb-3">Financing</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-5">Flexible Financing Options</h1>
          <p className="text-xl text-white/75 max-w-2xl mx-auto">0% APR financing, fast approvals, and monthly payments that fit your budget. Don&apos;t delay comfort.</p>
        </div>
      </section>

      <section className="py-16 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-timer-flash-line', title: '60-Second Approvals', desc: 'Most applicants approved in under 60 seconds.' },
              { icon: 'ri-percent-line', title: '0% APR Available', desc: '12 and 24-month no-interest options on qualifying systems.' },
              { icon: 'ri-money-dollar-circle-line', title: '$0 Down', desc: 'No down payment required on qualifying projects.' },
              { icon: 'ri-coupon-3-line', title: 'Stack with Rebates', desc: 'Combine financing with federal and utility rebates.' },
            ].map((b) => (
              <div key={b.title} className="text-center p-6 bg-[#F8F9FB] rounded-xl border border-[#E2E8F0]">
                <div className="w-12 h-12 bg-[#0C2340]/8 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${b.icon} text-2xl text-[#0C2340]`} />
                </div>
                <h3 className="text-sm font-bold text-[#111827] mb-2">{b.title}</h3>
                <p className="text-xs text-[#6B7280]">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-8">Financing Plans</h2>
              <div className="space-y-4 mb-10">
                {options.map((o) => (
                  <div key={o.term} className="flex items-center justify-between p-5 bg-[#F8F9FB] rounded-xl border border-[#E2E8F0]">
                    <div>
                      <p className="text-sm font-bold text-[#111827]">{o.term} · {o.apr}</p>
                      <p className="text-xs text-[#6B7280] mt-0.5">{o.note}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#C41E3A]">{o.monthly}</p>
                      <p className="text-xs text-[#9CA3AF]">Est. on {o.total}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-[#0C2340] rounded-xl p-6 text-white">
                <h3 className="font-bold text-lg mb-3">Government Rebates</h3>
                <p className="text-white/70 text-sm mb-4">The Inflation Reduction Act provides up to $3,200/year in tax credits for qualifying energy upgrades including heat pumps, insulation, and electrical panels.</p>
                <button onClick={() => navigate('/rebates-incentives')} className="bg-white text-[#0C2340] px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#F8F9FB] transition-colors whitespace-nowrap cursor-pointer">
                  View Rebates & Incentives
                </button>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-6">Apply for Financing</h2>
              {status === 'success' ? (
                <div className="text-center py-12 bg-green-50 rounded-xl border border-green-200">
                  <i className="ri-check-circle-fill text-4xl text-green-500 mb-3" />
                  <h3 className="text-xl font-bold text-[#111827]">Application Submitted!</h3>
                  <p className="text-[#6B7280] mt-2">We&apos;ll contact you within one business day with your financing options.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" id="financing-form" className="space-y-4 bg-[#F8F9FB] rounded-2xl p-8 border border-[#E2E8F0]">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">First Name *</label>
                      <input name="first_name" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white" placeholder="Jane" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">Last Name *</label>
                      <input name="last_name" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white" placeholder="Smith" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Email Address *</label>
                    <input name="email" type="email" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white" placeholder="jane@example.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Phone Number *</label>
                    <input name="phone" type="tel" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white" placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Service Type *</label>
                    <select name="service_type" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white">
                      <option value="">Select service</option>
                      <option>HVAC Installation</option>
                      <option>Heat Pump Installation</option>
                      <option>Water Heater</option>
                      <option>Plumbing</option>
                      <option>Electrical</option>
                      <option>Insulation</option>
                      <option>Multiple Services</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Estimated Project Size</label>
                    <select name="project_size" className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white">
                      <option value="">Select range</option>
                      <option>$1,000 – $3,000</option>
                      <option>$3,000 – $7,000</option>
                      <option>$7,000 – $15,000</option>
                      <option>$15,000+</option>
                    </select>
                  </div>
                  {status === 'error' && <p className="text-sm text-red-600">Something went wrong. Please try again.</p>}
                  <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#C41E3A] text-white py-4 rounded-lg text-sm font-semibold hover:bg-[#A01830] transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer">
                    {status === 'submitting' ? 'Submitting...' : 'Submit Financing Application'}
                  </button>
                  <p className="text-xs text-[#9CA3AF] text-center">Checking eligibility will not affect your credit score.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default FinancingPage;
